import React, { useState } from "react";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { AppModal } from "components/modal";
import styled from "styled-components";
import { CallRecord } from "./CallRecordList";

interface CallRecordingSummaryProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly callRecords?: CallRecord[];
}

interface SummarySection {
  readonly title: string;
  readonly content: string[];
}

interface CallSummary {
  readonly id: string;
  readonly title: string;
  readonly sections: SummarySection[];
}

const SummaryHeader = styled.div`
  margin-bottom: 18px;
  padding: 12px 16px;
  border-left: 4px solid #2f80ed;
  background: #f3f7fc;
`;

const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.4;
`;

const SummarySectionContainer = styled.section`
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e6eaf0;

  &:last-child {
    border-bottom: 0;
  }
`;

const SummarySectionHeading = styled.h3`
  margin: 0 0 8px;
  padding-bottom: 6px;
  color: #2f80ed;
  font-size: 14px;
  font-weight: bold;
`;

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const SummaryListItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.55;

  &:last-child {
    margin-bottom: 0;
  }
`;

const renderSummaryPoint = (content: string) => {
  const separatorIndex = content.indexOf(":");

  if (separatorIndex === -1) {
    return content;
  }

  return (
    <>
      <strong>{content.slice(0, separatorIndex)}:</strong>
      {content.slice(separatorIndex + 1)}
    </>
  );
};

const callSummaries: CallSummary[] = [
  {
    id: "4237:1",
    title: "Remote blood pressure monitoring follow-up",
    sections: [
      {
        title: "Subjective",
        content: [
          'Chief Complaint: Remote blood pressure monitoring follow-up. Patient states, "I am doing good" and reports "headache I get sometimes in the evening."',
          "HPI: Mr. Denis Ferguson presents via remote telehealth to review home blood pressure logs. 70% of readings are above 140 mmHg systolic, above the target of <130 mmHg. He reports occasional evening headaches, morning or nighttime shivering/chills, mild infrequent presyncope/dizziness, and mild back pain. He denies edema, fever, dysuria, cough, sputum production, wheezing, hemoptysis, abdominal pain, nausea, vomiting, and diarrhea.",
          "Medications: Lisinopril 10 mg PO BID; Carvedilol 12.5 mg (prescribed TID, patient taking BID); Hydrochlorothiazide, dose unspecified. Allergies and past medical/surgical history were not explicitly mentioned.",
          "Social history: Restaurant meals and home-cooked meals are each approximately 50% of his diet."
        ]
      },
      {
        title: "Objective",
        content: [
          "Vital signs were not explicitly stated. Physical examination was not performed during this remote telehealth encounter.",
          "Creatinine is stable at 1.5 mg/dL (historical baseline 1.3-1.5 mg/dL). Potassium is within normal limits."
        ]
      },
      {
        title: "Assessment",
        content: ["Primary diagnosis: Uncontrolled hypertension."]
      },
      {
        title: "Plan",
        content: [
          "Increase Lisinopril to 20 mg PO BID (two 10 mg tablets in the morning and two in the evening). Continue Carvedilol 12.5 mg PO BID and Hydrochlorothiazide.",
          "Follow a low-sodium DASH/Mediterranean-style diet with more home-cooked meals, fruits, vegetables, and grains, favoring white meat over red meat. Restrict high-potassium foods due to the increased Lisinopril dosage.",
          "Obtain a Basic Metabolic Panel in two weeks to monitor creatinine and potassium.",
          "Avoid all NSAIDs, including Advil, Aleve, Motrin, Ibuprofen, Bextra, Mobic, Vioxx, Voltaren, and Naproxen. Hold Lisinopril during significant dehydration from nausea, vomiting, or diarrhea and consume broth to maintain volume status.",
          "Reduce Lisinopril back to 10 mg PO BID and contact the clinic if severe dizziness or lightheadedness occurs. Follow up in a couple of weeks to review blood pressure logs and repeat lab results."
        ]
      },
      {
        title: "Patient Summary (After-Visit Instructions)",
        content: [
          "Take two 10 mg Lisinopril tablets in the morning and two in the evening (20 mg twice daily). Continue Carvedilol and Hydrochlorothiazide as prescribed.",
          "Cook and eat at home more often to lower salt intake. Focus on vegetables, grains, and white meat, and limit red meat and high-potassium foods.",
          "Do not take Advil, Aleve, Motrin, Ibuprofen, Naproxen, Mobic, or Voltaren. If severe nausea, vomiting, or diarrhea occurs, stop Lisinopril until better and drink broth to stay hydrated.",
          "Have blood work completed in two weeks and continue checking blood pressure twice daily. If severe dizziness or lightheadedness occurs, take 10 mg Lisinopril twice daily and call the office. Follow up in a couple of weeks."
        ]
      }
    ]
  },
  {
    id: "4236:1",
    title: "Kidney disease and blood pressure follow-up",
    sections: [
      {
        title: "Subjective",
        content: [
          'Chief Complaint: "sometimes I am feeling little bit tired"',
          "HPI: The patient follows up approximately one month after the last visit for kidney disease and blood pressure. Blood pressure fluctuates from the 130s-140s to the 70s-80s once or twice weekly, usually in the morning and sometimes in the evening. These episodes cause dizziness. He reports decreased appetite, fatigue requiring rest breaks, one episode of vomiting after medication, and intermittent lower-leg swelling after prolonged sitting.",
          "Past medical history: Advanced kidney failure. Medications include Metoprolol, an unspecified secondary blood pressure medication, and nutritional supplements (Ensure, Boost, or Nepro as needed). He denies current NSAID use."
        ]
      },
      {
        title: "Objective",
        content: [
          "Creatinine increased to 3.9 mg/dL and GFR decreased to 15 mL/min. Hemoglobin decreased from 12.4 to 11.0. Vital signs and physical examination were not explicitly stated."
        ]
      },
      {
        title: "Assessment",
        content: [
          "Advanced kidney failure with worsening uremic symptoms (fatigue, anorexia, and nausea) and declining renal function.",
          "Symptomatic hypotension and anemia of chronic kidney disease. Hypotension may be related to dehydration; renal artery stent stenosis was considered unlikely."
        ]
      },
      {
        title: "Plan",
        content: [
          "Hold Metoprolol if systolic blood pressure is in the 70s or 80s. During hypotension, drink a cup of salty broth unless leg swelling is present. Use Ensure, Boost, or Nepro when skipping meals and continue avoiding all NSAIDs.",
          "Defer EPO injection unless hemoglobin drops below 10. Check blood pressure twice daily while lying down, then sitting or standing after 1-2 minutes with the arm supported.",
          "Arrange a general surgery appointment within one week for placement of a Peritoneal Dialysis catheter. A refresher Kidney Smart class in Munster was offered. The patient elected Peritoneal Dialysis after discussing Hemodialysis and CAPD.",
          "Follow up in the office in 3 to 4 weeks."
        ]
      },
      {
        title: "Patient Summary (After-Visit Instructions)",
        content: [
          "Check blood pressure twice daily: once lying down, then again sitting or standing after 1-2 minutes with the arm supported.",
          "If the top blood pressure number is in the 70s or 80s, do not take Metoprolol. Drink a cup of salty broth unless your legs are swollen.",
          "Use Ensure, Boost, or Nepro when skipping meals. Continue avoiding Advil, Aleve, Motrin, Ibuprofen, and Naproxen.",
          "Prepare for Peritoneal Dialysis. The office will arrange a surgeon appointment within the next week; a Kidney Smart refresher class is available in Munster.",
          "Return in 3 to 4 weeks."
        ]
      }
    ]
  }
];

export const CallRecordingSummary: React.FC<CallRecordingSummaryProps> = ({
  visible,
  onClose,
  callRecords
}) => {
  const [selectedSummaryId, setSelectedSummaryId] = useState<string>();
  const selectedSummary = callSummaries.find(
    summary => summary.id === selectedSummaryId
  );

  const getRecordLabel = (summaryId: string) => {
    const record = callRecords?.find(
      item => String(item.id) === summaryId.split(":")[0]
    );

    if (!record) {
      return "Call date and duration unavailable";
    }

    const totalSeconds = Math.round(record.call_duration);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, "0");

    return `${record.date_p} - ${minutes}:${seconds} min`;
  };

  const closeModal = () => {
    setSelectedSummaryId(undefined);
    onClose();
  };

  return (
    <AppModal
      visible={visible}
      title={"Call Recording Summaries"}
      width={800}
      footer={false}
      onCancel={closeModal}
    >
      {selectedSummary ? (
        <>
          <AppButton
            type={ButtonType.Link}
            buttonContent="Back to call records"
            onClick={() => setSelectedSummaryId(undefined)}
            style={{ paddingLeft: 0, marginBottom: 10 }}
          />
          <SummaryHeader>
            <SummaryTitle>{selectedSummary.title}</SummaryTitle>
            <div style={{ marginTop: 4, color: "#5f6b7a", fontSize: 12 }}>
              Call date and duration: {getRecordLabel(selectedSummary.id)}
            </div>
          </SummaryHeader>
          {selectedSummary.sections.map(section => (
            <SummarySectionContainer key={section.title}>
              <SummarySectionHeading>{section.title}</SummarySectionHeading>
              <SummaryList>
                {section.content.map(item => (
                  <SummaryListItem key={item}>
                    {renderSummaryPoint(item)}
                  </SummaryListItem>
                ))}
              </SummaryList>
            </SummarySectionContainer>
          ))}
        </>
      ) : (
        <div>
          {callSummaries.map(summary => (
            <AppButton
              key={summary.id}
              type={ButtonType.Secondary}
              buttonContent={`${getRecordLabel(summary.id)} - ${summary.title}`}
              onClick={() => setSelectedSummaryId(summary.id)}
              style={{ display: "block", width: "100%", marginBottom: 10 }}
            />
          ))}
        </div>
      )}
    </AppModal>
  );
};
